interface TechRep {
  ct: number | null;
  efficiency: number | null;
  tm: number | null;
}

interface TechRepAssessment extends TechRep {
  rule: number | null;
}

function calcStd(nums: number[]): number | null {
  if (nums.length < 2) return null;
  const mean = nums.reduce((acc, v) => acc + v, 0) / nums.length;
  const variance = nums.reduce((acc, v) => acc + (v - mean) ** 2, 0) / (nums.length - 1);
  return Math.sqrt(variance);
}

export function selectOfflierCt(vals: Array<number | null>): number | null {
  const filtered = vals.filter((v): v is number => v != null);
  if (filtered.length === 0) return null;
  const mean = filtered.reduce((a, b) => a + b, 0) / filtered.length;
  const std = Math.sqrt(filtered.reduce((a, b) => a + (b - mean) ** 2, 0) / Math.max(filtered.length - 1, 1));
  if (filtered.length > 2 && std > 0.5) {
    const diffs = vals.map(v => (v == null ? -Infinity : Math.abs(mean - v)));
    const idx = diffs.indexOf(Math.max(...diffs));
    const offlier = vals[idx];
    const countOther = vals.filter(v => v !== offlier).length;
    if (countOther === 2) return offlier as number;
  }
  return null;
}

export function selectOfflierEf(vals: Array<number | null>): number {
  if (vals.length !== 3) return -1;
  const filtered = vals.filter((v): v is number => v != null);
  if (filtered.length <= 2) return -1;
  const deviations = vals.map(v => (v == null ? 0 : Math.abs(v - 2)));
  const max = Math.max(...deviations);
  if (max > 0.13) return deviations.indexOf(max);
  return -1;
}

export function assessTechReps(
  reps: TechRep[],
  assay: string,
  tmMin: number | null,
  tmMax: number | null,
  ctThreshold: number,
): { reps: TechRepAssessment[]; stdCt: number | null } {
  const ext = reps.map(r => ({ ...r, rule: null as number | null }));

  const mark = (idx: number, rule: number) => {
    if (ext[idx].rule == null) ext[idx].rule = rule;
  };

  ext.forEach((r, idx) => {
    if ((tmMin != null && r.tm != null && r.tm < tmMin) || (tmMax != null && r.tm != null && r.tm > tmMax)) {
      mark(idx, 0);
    }
  });

  ext.forEach((r, idx) => {
    if (r.rule == null && r.ct != null && r.ct >= ctThreshold) mark(idx, 1);
  });

  if (assay !== 'AY1') {
    ext.forEach((r, idx) => {
      if (r.rule == null && r.efficiency != null && (r.efficiency < 1.7 || r.efficiency > 2.3)) {
        mark(idx, 2);
      }
    });
  }

  const validCtIdx = ext.map((r, idx) => ({ r, idx })).filter(({ r }) => r.rule == null && r.ct != null);
  if (validCtIdx.length === 3) {
    const arr = validCtIdx.map(({ r }) => r.ct as number);
    const meanCt = arr.reduce((acc, v) => acc + v, 0) / 3;
    const stdCtTmp = Math.sqrt(arr.reduce((acc, v) => acc + (v - meanCt) ** 2, 0) / 2);
    if (stdCtTmp > 0.5) {
      const maxDevIdx = validCtIdx.reduce((prev, curr) =>
        Math.abs((curr.r.ct as number) - meanCt) > Math.abs((prev.r.ct as number) - meanCt) ? curr : prev,
      );
      mark(maxDevIdx.idx, 3);
    }
  }

  if (assay !== 'AY1') {
    const validEffIdx = ext.map((r, idx) => ({ r, idx })).filter(({ r }) => r.rule == null && r.efficiency != null);
    if (validEffIdx.length === 3) {
      const deviations = validEffIdx.map(({ r }) => Math.abs((r.efficiency as number) - 2));
      const maxDev = Math.max(...deviations);
      if (maxDev > 0.13) {
        const outlierIdx = validEffIdx[deviations.indexOf(maxDev)].idx;
        mark(outlierIdx, 4);
      }
    }
  }

  const kept = ext
    .filter(r => r.rule == null)
    .map(r => r.ct)
    .filter((v): v is number => v != null);
  const stdCt = calcStd(kept);

  return { reps: ext, stdCt };
}

export interface BioRepResult {
  ct: number | null;
}

export function assessBioRep(
  reps: TechRep[],
  assay: string,
  tmMin: number | null,
  tmMax: number | null,
  ctThreshold: number,
): { ct: number | null } {
  const { reps: assessed, stdCt } = assessTechReps(reps, assay, tmMin, tmMax, ctThreshold);
  const keptCts = assessed
    .filter(r => r.rule == null)
    .map(r => r.ct)
    .filter((v): v is number => v != null);
  if (keptCts.length >= 2 && stdCt != null && stdCt <= 1) {
    const mean = keptCts.reduce((a, b) => a + b, 0) / keptCts.length;
    return { ct: mean };
  }
  return { ct: null };
}
